// features/item-filter/ItemFilterBar.jsx
//
// The filter bar: item search + optional product-category, supplier and
// location filters + page-specific leading/actions/children.
//
// Layout is the shared <FilterRow> (FilterRow.css): one row that shrinks its
// boxes before it wraps, and stacks below the tablet breakpoint — the same row
// the plain-search pages use.
//
// The item search box is a `TypeaheadSearchField` — the owned input +
// suggestion popper shared with the Purchase orders page's PO-number box, so
// the two boxes on that row behave identically. This file wires it to
// `useItemFilter`; the widget owns focus, keyboard and popper state.
//
// All filter mutations — search commit/clear, category, supplier, location —
// run through `filter.guarded`, so pages with editable grids get exactly one
// unsaved-changes prompt per user action. Pass `beforeChange` to
// useItemFilter; do not gate the setters page-side.
//
// The supplier and location filters read their own lists (SupplierFilter,
// LocationFilter).

import React, { useCallback } from 'react';
import { Box, FormHelperText } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { translateRequestError } from '../../queries/api';
import { useIsShopify } from '../../contexts/AuthContext';
import { ItemOptionContent } from '../../components/common/ItemOption';
import ProductCategoryPicker from '../../components/common/ProductCategoryPicker';
import TypeaheadSearchField from '../../components/common/TypeaheadSearchField';
import FilterRow from '../../components/common/FilterRow';
import SupplierFilter from './SupplierFilter';
import LocationFilter from './LocationFilter';
import { useItemSearchCopy } from './useItemSearchCopy';

function ItemFilterBar({
  // Bundle returned by useItemFilter (directly or via useGridChartScope)
  filter,
  // { selected, set, show } — picker fetches its own options
  productCategoryFilter,
  // { selected, set } — omit on a page without a supplier filter. `selected`
  // is null or the picked { supplier_id, supplier_name }.
  supplierFilter,
  // { selected, set } — omit on a page without a location filter
  locationFilter,
  actions = null,
  // Page-specific filter rendered BEFORE the item search box. Use it when the
  // page's primary lookup is not the item — Purchase orders leads with the PO
  // number, because a merchant arriving with a number off a printed order,
  // a supplier email or the ERP reaches for the first box on the row.
  leading = null,
  children = null,
}) {
  const { t } = useTranslation();
  const isShopify = useIsShopify();
  const itemSearch = useItemSearchCopy();
  const showProductCategoryFilter = productCategoryFilter?.show ?? false;

  const renderItem = useCallback(
    (item) => <ItemOptionContent option={item} isShopify={isShopify} />,
    [isShopify],
  );

  const handleCategoryChange = useCallback((cat) => {
    filter.guarded(() => productCategoryFilter.set(cat));
  }, [filter, productCategoryFilter]);

  const handleSupplierChange = useCallback((supplier) => {
    filter.guarded(() => supplierFilter.set(supplier));
  }, [filter, supplierFilter]);

  const handleLocationChange = useCallback((value) => {
    filter.guarded(() => locationFilter.set(value));
  }, [filter, locationFilter]);

  return (
    <FilterRow actions={(children || actions) && <>{children}{actions}</>}>
      {/* Leading page-specific filter (e.g. Purchase orders' PO number) */}
      {leading}

      {/* Item search */}
      <TypeaheadSearchField
        className="filter-row-search"
        label={itemSearch.label}
        placeholder={itemSearch.placeholder}
        value={filter.inputText}
        committedText={filter.displayText}
        onChange={filter.onInputChange}
        suggestions={filter.suggestions}
        loading={filter.loading}
        failed={filter.suggestionsFailed}
        getOptionKey={(item) => item.item_id}
        renderOption={renderItem}
        onPick={filter.commitItem}
        onCommitText={filter.commitText}
        // The X drops the committed filter as well as the draft, no matter
        // how the filter was committed.
        showClear={filter.inputText !== '' || filter.filter !== null}
        onClear={filter.clear}
        emptyText={t('searchFilters.noItemsFound')}
        footerText={t('searchFilters.itemSearchHelper')}
      />
      {/* An item opened by its code whose name could not be read: the
          filter still applies, so the row says why the box is empty. */}
      {filter.labelError && (
        <FormHelperText error className="filter-row-slot">
          {translateRequestError(filter.labelError, t, 'searchFilters.itemLabelFailed')}
        </FormHelperText>
      )}

      {/* Product Category Filter (server-side typeahead) */}
      {showProductCategoryFilter && (
        <Box className="filter-row-slot">
          <ProductCategoryPicker
            scopeToStore
            value={productCategoryFilter.selected}
            onChange={handleCategoryChange}
            allLabel={t('searchFilters.productCategoryAllLabel')}
            size="small"
          />
        </Box>
      )}

      {supplierFilter && (
        <SupplierFilter selected={supplierFilter.selected} onChange={handleSupplierChange} />
      )}

      {locationFilter && (
        <LocationFilter selected={locationFilter.selected} onChange={handleLocationChange} />
      )}
    </FilterRow>
  );
}

export default ItemFilterBar;
